import { useQuery } from '@apollo/client';
import { useAuth } from '@clerk/clerk-react';
import { useMemo } from 'react';
import { Tooltip } from 'primereact/tooltip';
import { GET_COMPANY_METADATA } from '~/graphql/queries/company';

interface CompanyMetadata {
  run_usage?: Record<string, { remaining_runs: number }>;
  runs_per_day?: number;
  total_runs_remaining?: number;
  pilot_end_date?: string;
}

interface Company {
  id: string;
  name: string;
  clerkSlug: string;
  metadata?: CompanyMetadata;
}

interface GetCompanyMetadataResponse {
  Company: Company[];
}

export const PilotStatus: React.FC = () => {
  const auth = useAuth();
  const clerkId = auth.orgId;

  const { data, loading, error } = useQuery<GetCompanyMetadataResponse>(
    GET_COMPANY_METADATA,
    {
      variables: { clerkId },
      skip: !clerkId,
      pollInterval: 30000, // Poll every 30 seconds to keep data fresh
    },
  );

  const pilotData = useMemo(() => {
    if (!data?.Company?.[0]?.metadata) return null;

    const metadata = data.Company[0].metadata;
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    const todayFormatted = today.split('-').reverse().join('-'); // DD-MM-YYYY format

    // Calculate business days remaining in pilot
    const pilotEndDate = metadata.pilot_end_date;
    let daysRemaining = 0;
    if (pilotEndDate) {
      const endDate = new Date(pilotEndDate);
      const currentDate = new Date();

      // Calculate business days between current date and end date
      let businessDays = 0;
      const tempDate = new Date(currentDate);

      while (tempDate <= endDate) {
        const dayOfWeek = tempDate.getDay();
        // Count only weekdays (Monday = 1, Friday = 5)
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          businessDays++;
        }
        tempDate.setDate(tempDate.getDate() + 1);
      }

      daysRemaining = Math.max(0, businessDays);
    }

    // Determine run mode and calculate runs remaining
    const isUsingTotalRuns =
      metadata.total_runs_remaining !== undefined &&
      metadata.total_runs_remaining !== null;

    let runsRemaining: number;
    let runsPerDay: number | null = null;
    let runMode: 'daily' | 'total';

    if (isUsingTotalRuns) {
      // Total runs mode
      runMode = 'total';
      runsRemaining = metadata.total_runs_remaining || 0;
    } else {
      // Daily runs mode (fallback)
      runMode = 'daily';
      const runUsage = metadata.run_usage || {};
      const todayUsage = runUsage[todayFormatted] || {
        remaining_runs: metadata.runs_per_day || 0,
      };
      runsRemaining = todayUsage.remaining_runs;
      runsPerDay = metadata.runs_per_day || 20;
    }

    return {
      daysRemaining,
      runsRemaining,
      runsPerDay,
      runMode,
    };
  }, [data]);

  if (loading) {
    return (
      <div
        className="flex align-items-center gap-2 text-sm"
        style={{ color: 'var(--bq-text-secondary)' }}
      >
        <i className="pi pi-spin pi-spinner"></i>
        <span>Loading pilot status...</span>
      </div>
    );
  }

  if (error || !pilotData) {
    return null;
  }

  return (
    <div className="flex align-items-center gap-3 text-sm">
      {/* Days Remaining */}
      <div className="flex align-items-center gap-1">
        <i
          className="pi pi-calendar"
          style={{
            color:
              pilotData.daysRemaining <= 3
                ? 'var(--bq-color-danger)'
                : 'var(--bq-color-info)',
          }}
        ></i>
        <span style={{ color: 'var(--bq-text-secondary)' }}>
          Pilot days remaining:
        </span>
        <span
          className="font-semibold"
          style={{
            color:
              pilotData.daysRemaining <= 3
                ? 'var(--bq-color-danger)'
                : 'var(--bq-color-info)',
          }}
        >
          {pilotData.daysRemaining}
        </span>
      </div>

      {/* Runs Remaining */}
      <div className="flex align-items-center gap-1">
        <i
          className="pi pi-play"
          style={{
            color:
              pilotData.runsRemaining <= 2
                ? 'var(--bq-color-danger)'
                : 'var(--bq-color-info)',
          }}
        ></i>
        <span style={{ color: 'var(--bq-text-secondary)' }}>
          {pilotData.runMode === 'total' ? 'Runs remaining:' : 'Runs today:'}
        </span>
        <span
          className="font-semibold"
          style={{
            color:
              pilotData.runsRemaining <= 2
                ? 'var(--bq-color-danger)'
                : 'var(--bq-color-info)',
          }}
        >
          {pilotData.runMode === 'total'
            ? `${pilotData.runsRemaining}`
            : `${pilotData.runsRemaining} / ${pilotData.runsPerDay}`}
        </span>
      </div>

      {/* Disclaimer Tooltip */}
      <div className="flex align-items-center">
        <i
          id="disclaimer-tooltip-trigger"
          className="pi pi-info-circle cursor-pointer"
          style={{
            fontSize: '0.875rem',
            color: 'var(--bq-color-warning)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--bq-color-warning-darker)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'var(--bq-color-warning)';
          }}
        ></i>
        <Tooltip
          target="#disclaimer-tooltip-trigger"
          position="bottom"
          style={{ maxWidth: '300px' }}
        >
          <div className="text-xs">
            <strong style={{ color: 'var(--bq-color-warning)' }}>
              Disclaimer:
            </strong>
            <br />
            This application uses publicly available news and market data -
            Bloomberg connectivity and internal system integration is available.
            Metrics may not be in line with internal data. Not investment
            advice.
          </div>
        </Tooltip>
      </div>
    </div>
  );
};
